// economicAPI.js
// Servicio para manejar la obtención de datos económicos

const CACHE_KEY = 'economicData';
const CACHE_TIME_KEY = 'economicDataTime';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutos en milisegundos

/**
 * Verifica si hay datos en caché y si son válidos
 * @returns {Object|null} Los datos en caché o null si no hay o expiró
 */
const getFromCache = () => {
  try {
    const cachedData = JSON.parse(localStorage.getItem(CACHE_KEY));
    const cacheTime = localStorage.getItem(CACHE_TIME_KEY);
    
    if (!cachedData || !cacheTime) return null;
    
    // Verificar si el caché ha expirado (30 minutos)
    const isCacheValid = (Date.now() - parseInt(cacheTime)) < CACHE_DURATION;
    
    return isCacheValid ? cachedData : null;
  } catch (error) {
    console.error("Error al leer caché:", error);
    return null;
  }
};

/**
 * Guarda los datos en caché con timestamp
 * @param {Object} data Los datos a guardar
 */
const saveToCache = (data) => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    localStorage.setItem(CACHE_TIME_KEY, Date.now().toString());
  } catch (error) {
    console.error("Error al guardar en caché:", error);
  }
};

/**
 * Obtiene los indicadores económicos, primero intentando desde caché
 * @returns {Promise<Object>} Los datos económicos
 */
export const getEconomicIndicators = async () => {
  // Primero intentamos obtener del caché
  const cachedData = getFromCache();
  if (cachedData) {
    return {
      ...cachedData,
      fromCache: true
    };
  }
  
  // Si no hay caché válido, hacemos la petición
  try {
    const response = await fetch("https://mindicador.cl/api");
    
    if (!response.ok) {
      throw new Error(`Error en la respuesta: ${response.status}`);
    }
    
    const data = await response.json();
    
    // Formatear la fecha
    const apiDate = new Date(data.fecha);
    const formattedDate = apiDate.toLocaleDateString("es-CL", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
    
    // Crear objeto con datos relevantes
    const economicData = {
      uf: data.uf?.valor,
      dollar: data.dolar?.valor,
      date: formattedDate
    };
    
    // Guardar en caché
    saveToCache(economicData);
    
    return economicData;
  } catch (error) {
    console.error("Error al obtener indicadores económicos:", error);
    throw error;
  }
};

export default {
  getEconomicIndicators
};